import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'

let storeAuth = null
let realtimeSubscription = null

/**
 * Club visibility levels
 * Controls who can see the club and its content
 */
export const CLUB_VISIBILITY = {
  HIDDEN: 'hidden', // Only members can see the club exists
  MEMBERS_ONLY: 'members_only', // Discoverable but content restricted to members
  EVERYONE: 'everyone' // Fully public
}

/**
 * Club join modes
 * Controls how users can join the club (independent of visibility)
 */
export const CLUB_JOIN_MODE = {
  OPEN: 'open', // Anyone can join freely
  REQUEST: 'request', // Must request to join, admin approves
  INVITE: 'invite' // Invite-only, no public requests
}

/**
 * Content visibility levels (for reviews, lists, etc.)
 */
export const CONTENT_VISIBILITY = {
  ONLY_ME: 'only_me', // Private - only you can see
  FOLLOWERS: 'followers', // Followers only
  EVERYONE: 'everyone' // Public - anyone can see
}

/**
 * Club member roles
 */
export const CLUB_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member'
}

/**
 * Club member statuses
 */
export const MEMBER_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
  INVITED: 'invited'
}

export const useStoreClubs = defineStore('storeClubs', {
  state: () => ({
    // User's clubs (member of)
    myClubs: [],
    myClubsLoaded: false,

    // Discovery/browse clubs
    discoverClubs: [],
    discoverClubsLoaded: false,
    discoverHasMore: false,
    discoverOffset: 0,

    // Search results
    searchResults: [],
    searchQuery: '',
    searchLoading: false,

    // Current club being viewed
    currentClub: null,
    currentClubLoading: false,

    // Current club's posts
    currentClubPosts: [],
    currentClubPostsLoading: false,
    currentClubPostsHasMore: false,
    currentClubPostsOffset: 0,

    // Current club's members
    currentClubMembers: [],
    currentClubMembersLoading: false,

    // Current club's watchlist
    currentClubWatchlist: [],
    currentClubWatchlistLoading: false,

    // Current club's lists (from general lists system)
    currentClubLists: [],
    currentClubListsLoading: false,

    // Join requests (for admins)
    joinRequests: [],
    joinRequestsLoading: false,

    // Profile clubs (for viewing any user's clubs)
    profileClubs: {},
    profileClubsLoading: {},

    // General state
    loading: false,
    error: null
  }),

  getters: {
    /**
     * Get clubs where user is owner
     */
    clubsWhereOwner: (state) => {
      return state.myClubs.filter((c) => c.user_role === CLUB_ROLES.OWNER)
    },

    /**
     * Get clubs where user is admin (owner or admin role)
     */
    clubsWhereAdmin: (state) => {
      return state.myClubs.filter(
        (c) =>
          c.user_role === CLUB_ROLES.OWNER || c.user_role === CLUB_ROLES.ADMIN
      )
    },

    /**
     * Get clubs where user is regular member
     */
    clubsWhereMember: (state) => {
      return state.myClubs.filter((c) => c.user_role === CLUB_ROLES.MEMBER)
    },

    /**
     * Check if user is member of a club
     */
    isClubMember: (state) => (clubId) => {
      return state.myClubs.some((c) => c.id === clubId)
    },

    /**
     * Check if user is admin of a club
     */
    isClubAdmin: (state) => (clubId) => {
      const club = state.myClubs.find((c) => c.id === clubId)
      return (
        club?.user_role === CLUB_ROLES.OWNER ||
        club?.user_role === CLUB_ROLES.ADMIN
      )
    },

    /**
     * Get user's role in a club
     */
    getMemberRole: (state) => (clubId) => {
      const club = state.myClubs.find((c) => c.id === clubId)
      return club?.user_role || null
    },

    /**
     * Check if clubs feature is loading
     */
    isLoading: (state) => state.loading,

    /**
     * Count of clubs user owns
     */
    ownedClubsCount: (state) => {
      return state.myClubs.filter((c) => c.user_role === CLUB_ROLES.OWNER)
        .length
    }
  },

  actions: {
    /**
     * Initialize the clubs store
     */
    init() {
      storeAuth = useStoreAuth()
      if (!storeAuth.user) {
        console.log('[storeClubs] Skipping init - user not authenticated')
        return
      }
      this.fetchMyClubs()
    },

    /**
     * Fetch user's clubs (clubs they are a member of)
     */
    async fetchMyClubs() {
      storeAuth = useStoreAuth()
      if (!storeAuth.user) return

      this.loading = true
      this.error = null

      try {
        // Use the view which includes user's membership info
        const { data, error } = await supabase
          .from('clubs_with_details')
          .select('*')
          .not('user_role', 'is', null) // Only clubs where user has a role
          .eq('user_status', MEMBER_STATUS.ACTIVE)
          .order('created_at', { ascending: false })

        if (error) throw error

        this.myClubs = data || []
        this.myClubsLoaded = true
      } catch (err) {
        console.error('[storeClubs] Error fetching my clubs:', err)
        this.error = err.message
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch clubs for a specific user's profile
     * @param {string} userId - The user ID to fetch clubs for
     * @param {boolean} isOwnProfile - If true, show all clubs; otherwise only public ones
     */
    async fetchProfileClubs(userId, isOwnProfile = false) {
      if (!userId) return

      this.profileClubsLoading[userId] = true

      try {
        // Query club_members to find clubs this user belongs to
        let query = supabase
          .from('club_members')
          .select(
            `
            club_id,
            role,
            status,
            clubs!inner(
              id,
              name,
              slug,
              description,
              cover_url,
              visibility,
              join_mode,
              created_at,
              member_count
            )
          `
          )
          .eq('user_id', userId)
          .eq('status', MEMBER_STATUS.ACTIVE)

        // If not viewing own profile, only show public clubs
        if (!isOwnProfile) {
          query = query.eq('clubs.visibility', CLUB_VISIBILITY.EVERYONE)
        }

        const { data, error } = await query.order('created_at', {
          foreignTable: 'clubs',
          ascending: false
        })

        if (error) throw error

        // Extract club data with member info
        const clubs = (data || []).map((item) => ({
          ...item.clubs,
          user_role: item.role,
          user_status: item.status
        }))

        this.profileClubs[userId] = clubs
      } catch (err) {
        console.error('[storeClubs] Error fetching profile clubs:', err)
        this.profileClubs[userId] = []
      } finally {
        this.profileClubsLoading[userId] = false
      }
    },

    /**
     * Get clubs for a specific user
     * @param {string} userId - The user ID
     * @returns {Array} Array of clubs
     */
    getProfileClubs(userId) {
      return this.profileClubs[userId] || []
    },

    /**
     * Fetch clubs for discovery (public clubs)
     * @param {object} options - Fetch options
     * @param {boolean} options.refresh - Reset pagination
     * @param {string} options.visibility - Filter by visibility level
     * @param {string} options.join_mode - Filter by join mode
     */
    async fetchDiscoverClubs(options = {}) {
      const { refresh = false, visibility = null, join_mode = null } = options

      if (refresh) {
        this.discoverClubs = []
        this.discoverOffset = 0
        this.discoverHasMore = true
      }

      if (!this.discoverHasMore && !refresh) return

      this.loading = true
      this.error = null

      const limit = 20

      try {
        let query = supabase
          .from('clubs_with_details')
          .select('*')
          .order('member_count', { ascending: false })
          .range(this.discoverOffset, this.discoverOffset + limit)

        // Filter by visibility
        if (visibility) {
          query = query.eq('visibility', visibility)
        } else {
          // Show everyone and members_only (not hidden) for browse
          query = query.in('visibility', [
            CLUB_VISIBILITY.EVERYONE,
            CLUB_VISIBILITY.MEMBERS_ONLY
          ])
        }

        // Optional filter by join mode
        if (join_mode) {
          query = query.eq('join_mode', join_mode)
        }

        const { data, error } = await query

        if (error) throw error

        const clubs = data || []

        if (refresh) {
          this.discoverClubs = clubs
        } else {
          this.discoverClubs = [...this.discoverClubs, ...clubs]
        }

        this.discoverHasMore = clubs.length > limit
        if (this.discoverHasMore) {
          this.discoverClubs.pop()
        }

        this.discoverOffset += clubs.length
        this.discoverClubsLoaded = true
      } catch (err) {
        console.error('[storeClubs] Error fetching discover clubs:', err)
        this.error = err.message
      } finally {
        this.loading = false
      }
    },

    /**
     * Search clubs by name and description
     * @param {string} query - Search query
     */
    async searchClubs(query) {
      if (!query || query.length < 2) {
        this.searchResults = []
        this.searchQuery = ''
        return
      }

      this.searchQuery = query
      this.searchLoading = true

      try {
        const { data, error } = await supabase
          .from('clubs_with_details')
          .select('*')
          .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
          .in('visibility', [
            CLUB_VISIBILITY.EVERYONE,
            CLUB_VISIBILITY.MEMBERS_ONLY
          ])
          .order('member_count', { ascending: false })
          .limit(20)

        if (error) throw error

        this.searchResults = data || []
      } catch (err) {
        console.error('[storeClubs] Error searching clubs:', err)
        this.error = err.message
      } finally {
        this.searchLoading = false
      }
    },

    /**
     * Fetch a single club by slug
     * @param {string} slug - Club slug
     */
    async fetchClub(slug) {
      this.currentClubLoading = true
      this.error = null

      try {
        const { data, error } = await supabase
          .from('clubs_with_details')
          .select('*')
          .eq('slug', slug)
          .single()

        if (error) {
          if (error.code === 'PGRST116') {
            // Not found
            this.error = 'Club not found'
            this.currentClub = null
            return null
          }
          throw error
        }

        this.currentClub = data
        return data
      } catch (err) {
        console.error('[storeClubs] Error fetching club:', err)
        this.error = err.message
        return null
      } finally {
        this.currentClubLoading = false
      }
    },

    /**
     * Create a new club
     * @param {object} data - Club data
     * @returns {object|null} Created club or null on error
     */
    async createClub(data) {
      storeAuth = useStoreAuth()
      if (!storeAuth.user) return null

      this.loading = true
      this.error = null

      try {
        // Call edge function to create club
        const { data: result, error } = await supabase.functions.invoke(
          'clubs',
          {
            method: 'POST',
            body: data
          }
        )

        if (error) throw error

        if (result.error) {
          this.error = result.error
          return null
        }

        // Track club creation in PostHog
        if (import.meta.env.VITE_APP_MODE === 'production') {
          import('@/composables/usePostHog').then(({ usePostHog }) => {
            const { track } = usePostHog()
            track('Club Created', {
              is_public: Boolean(data.public),
              has_description: Boolean(data.description)
            })
          })
        }

        // Refresh my clubs list
        await this.fetchMyClubs()

        return result.club
      } catch (err) {
        console.error('[storeClubs] Error creating club:', err)
        this.error = err.message
        return null
      } finally {
        this.loading = false
      }
    },

    /**
     * Update a club
     * @param {string} slug - Club slug
     * @param {object} data - Updated data
     * @returns {object|null} Updated club or null on error
     */
    async updateClub(slug, data) {
      this.loading = true
      this.error = null

      try {
        const { data: result, error } = await supabase.functions.invoke(
          `clubs/${slug}`,
          {
            method: 'PUT',
            body: data
          }
        )

        if (error) throw error

        if (result.error) {
          this.error = result.error
          return null
        }

        // Update in local state
        const index = this.myClubs.findIndex((c) => c.slug === slug)
        if (index !== -1) {
          this.myClubs[index] = { ...this.myClubs[index], ...result.club }
        }

        if (this.currentClub?.slug === slug) {
          this.currentClub = { ...this.currentClub, ...result.club }
        }

        return result.club
      } catch (err) {
        console.error('[storeClubs] Error updating club:', err)
        this.error = err.message
        return null
      } finally {
        this.loading = false
      }
    },

    /**
     * Delete a club (soft delete)
     * @param {string} slug - Club slug
     * @returns {boolean} Success status
     */
    async deleteClub(slug) {
      this.loading = true
      this.error = null

      try {
        const { data: result, error } = await supabase.functions.invoke(
          'clubs',
          {
            method: 'DELETE',
            body: { slug }
          }
        )

        if (error) throw error

        if (result.error) {
          this.error = result.error
          return false
        }

        // Remove from local state
        this.myClubs = this.myClubs.filter((c) => c.slug !== slug)

        if (this.currentClub?.slug === slug) {
          this.currentClub = null
        }

        return true
      } catch (err) {
        console.error('[storeClubs] Error deleting club:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Join a public club
     * @param {string} slug - Club slug
     * @returns {boolean} Success status
     */
    async joinClub(slug) {
      this.loading = true
      this.error = null

      try {
        const { data: result, error } = await supabase.functions.invoke(
          'clubs',
          {
            method: 'POST',
            body: { action: 'join', slug }
          }
        )

        if (error) throw error

        if (result.error) {
          this.error = result.error
          return false
        }

        // Track club join in PostHog
        if (import.meta.env.VITE_APP_MODE === 'production') {
          import('@/composables/usePostHog').then(({ usePostHog }) => {
            const { track } = usePostHog()
            track('Club Joined', {
              club_slug: slug
            })
          })
        }

        // Refresh my clubs
        await this.fetchMyClubs()

        // Refresh current club if viewing it
        if (this.currentClub?.slug === slug) {
          await this.fetchClub(slug)
        }

        return true
      } catch (err) {
        console.error('[storeClubs] Error joining club:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Leave a club
     * @param {string} slug - Club slug
     * @returns {boolean} Success status
     */
    async leaveClub(slug) {
      this.loading = true
      this.error = null

      try {
        const { data: result, error } = await supabase.functions.invoke(
          'clubs',
          {
            method: 'POST',
            body: { action: 'leave', slug }
          }
        )

        if (error) throw error

        if (result.error) {
          this.error = result.error
          return false
        }

        // Track club leave in PostHog
        if (import.meta.env.VITE_APP_MODE === 'production') {
          import('@/composables/usePostHog').then(({ usePostHog }) => {
            const { track } = usePostHog()
            track('Club Left', {
              club_slug: slug
            })
          })
        }

        // Remove from my clubs
        this.myClubs = this.myClubs.filter((c) => c.slug !== slug)

        // Refresh current club if viewing it
        if (this.currentClub?.slug === slug) {
          await this.fetchClub(slug)
        }

        return true
      } catch (err) {
        console.error('[storeClubs] Error leaving club:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Request to join a private club
     * @param {string} slug - Club slug
     * @returns {boolean} Success status
     */
    async requestToJoin(slug) {
      this.loading = true
      this.error = null

      try {
        const { data: result, error } = await supabase.functions.invoke(
          'clubs',
          {
            method: 'POST',
            body: { action: 'request_join', slug }
          }
        )

        if (error) throw error

        if (result.error) {
          this.error = result.error
          return false
        }

        // Refresh current club to show pending status
        if (this.currentClub?.slug === slug) {
          await this.fetchClub(slug)
        }

        return true
      } catch (err) {
        console.error('[storeClubs] Error requesting to join:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Cancel a pending join request
     * @param {string} slug - Club slug
     * @returns {boolean} Success status
     */
    async cancelJoinRequest(slug) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/request`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        const result = await response.json()

        if (!response.ok) {
          this.error = result.error || 'Failed to cancel request'
          return false
        }

        // Refresh current club to clear pending status
        if (this.currentClub?.slug === slug) {
          await this.fetchClub(slug)
        }

        return true
      } catch (err) {
        console.error('[storeClubs] Error cancelling join request:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch club members
     * @param {string} slug - Club slug
     */
    async fetchClubMembers(slug) {
      this.currentClubMembersLoading = true

      try {
        const { data, error } = await supabase.functions.invoke('clubs', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          body: null
        })

        // Use fetch directly since invoke doesn't support GET with path params well
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/members`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch members')
        }

        const result = await response.json()
        this.currentClubMembers = result.members || []
      } catch (err) {
        console.error('[storeClubs] Error fetching members:', err)
        this.error = err.message
      } finally {
        this.currentClubMembersLoading = false
      }
    },

    /**
     * Update a member's role
     * @param {string} slug - Club slug
     * @param {string} userId - User ID to update
     * @param {string} role - New role (admin or member)
     * @returns {boolean} Success status
     */
    async updateMemberRole(slug, userId, role) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/members/${userId}/role`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ role })
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to update role'
          return false
        }

        // Refresh members list
        await this.fetchClubMembers(slug)
        return true
      } catch (err) {
        console.error('[storeClubs] Error updating member role:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Remove a member from the club
     * @param {string} slug - Club slug
     * @param {string} userId - User ID to remove
     * @returns {boolean} Success status
     */
    async removeMember(slug, userId) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/members/${userId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to remove member'
          return false
        }

        // Update local state
        this.currentClubMembers = this.currentClubMembers.filter(
          (m) => m.user_id !== userId
        )

        // Refresh club to update member count
        if (this.currentClub?.slug === slug) {
          await this.fetchClub(slug)
        }

        return true
      } catch (err) {
        console.error('[storeClubs] Error removing member:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch join requests for a club (admin only)
     * @param {string} slug - Club slug
     */
    async fetchJoinRequests(slug) {
      this.joinRequestsLoading = true

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/requests`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch join requests')
        }

        const result = await response.json()
        this.joinRequests = result.requests || []
      } catch (err) {
        console.error('[storeClubs] Error fetching join requests:', err)
        this.error = err.message
      } finally {
        this.joinRequestsLoading = false
      }
    },

    /**
     * Approve a join request
     * @param {string} slug - Club slug
     * @param {string} requestId - Request ID
     * @returns {boolean} Success status
     */
    async approveJoinRequest(slug, requestId) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/requests/${requestId}/approve`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to approve request'
          return false
        }

        // Remove from local state
        this.joinRequests = this.joinRequests.filter((r) => r.id !== requestId)

        // Refresh members and club
        await Promise.all([this.fetchClubMembers(slug), this.fetchClub(slug)])

        return true
      } catch (err) {
        console.error('[storeClubs] Error approving request:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Reject a join request
     * @param {string} slug - Club slug
     * @param {string} requestId - Request ID
     * @returns {boolean} Success status
     */
    async rejectJoinRequest(slug, requestId) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/requests/${requestId}/reject`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to reject request'
          return false
        }

        // Remove from local state
        this.joinRequests = this.joinRequests.filter((r) => r.id !== requestId)

        return true
      } catch (err) {
        console.error('[storeClubs] Error rejecting request:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch posts for the current club
     * @param {string} slug - Club slug
     * @param {object} options - Fetch options
     * @param {boolean} options.refresh - Reset pagination
     */
    async fetchClubPosts(slug, options = {}) {
      const { refresh = false } = options

      if (refresh) {
        this.currentClubPosts = []
        this.currentClubPostsOffset = 0
        this.currentClubPostsHasMore = true
      }

      if (!this.currentClubPostsHasMore && !refresh) return

      this.currentClubPostsLoading = true

      const limit = 20

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/posts?limit=${limit}&offset=${this.currentClubPostsOffset}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch posts')
        }

        const result = await response.json()
        const posts = result.posts || []

        if (refresh) {
          this.currentClubPosts = posts
        } else {
          this.currentClubPosts = [...this.currentClubPosts, ...posts]
        }

        this.currentClubPostsHasMore = result.has_more || false
        this.currentClubPostsOffset += posts.length
      } catch (err) {
        console.error('[storeClubs] Error fetching posts:', err)
        this.error = err.message
      } finally {
        this.currentClubPostsLoading = false
      }
    },

    /**
     * Delete a post
     * @param {string} slug - Club slug
     * @param {string} postId - Post ID
     * @returns {boolean} Success status
     */
    async deletePost(slug, postId) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/posts/${postId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to delete post'
          return false
        }

        // Remove from local state
        this.currentClubPosts = this.currentClubPosts.filter(
          (p) => p.id !== postId
        )

        return true
      } catch (err) {
        console.error('[storeClubs] Error deleting post:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Add a new post to the local state (optimistic update)
     * @param {object} post - Post object
     */
    addPost(post) {
      this.currentClubPosts = [post, ...this.currentClubPosts]
    },

    /**
     * Fetch watchlist for the current club
     * @param {string} slug - Club slug
     */
    async fetchClubWatchlist(slug) {
      this.currentClubWatchlistLoading = true

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/watchlist`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch watchlist')
        }

        const result = await response.json()
        this.currentClubWatchlist = result.items || []
      } catch (err) {
        console.error('[storeClubs] Error fetching watchlist:', err)
        this.error = err.message
      } finally {
        this.currentClubWatchlistLoading = false
      }
    },

    /**
     * Fetch club's lists (from general lists system)
     * @param {string} slug - Club slug
     */
    async fetchClubLists(slug) {
      this.currentClubListsLoading = true

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/lists`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch club lists')
        }

        const result = await response.json()
        this.currentClubLists = result.lists || []
      } catch (err) {
        console.error('[storeClubs] Error fetching club lists:', err)
        this.error = err.message
      } finally {
        this.currentClubListsLoading = false
      }
    },

    /**
     * Add a title to the club watchlist
     * @param {string} slug - Club slug
     * @param {object} titleData - Title data from TMDB
     * @returns {boolean} Success status
     */
    async addToWatchlist(slug, titleData) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/watchlist`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(titleData)
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to add to watchlist'
          return false
        }

        const result = await response.json()

        // Add to local state
        this.currentClubWatchlist = [
          { ...result.item, title_id: result.item.tmdb_id },
          ...this.currentClubWatchlist
        ]

        return true
      } catch (err) {
        console.error('[storeClubs] Error adding to watchlist:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Update watchlist item status
     * @param {string} slug - Club slug
     * @param {string} itemId - Watchlist item ID
     * @param {string} status - New status
     * @returns {boolean} Success status
     */
    async updateWatchlistStatus(slug, itemId, status) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/watchlist/${itemId}`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status })
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to update watchlist item'
          return false
        }

        // Update local state
        const index = this.currentClubWatchlist.findIndex(
          (i) => i.id === itemId
        )
        if (index !== -1) {
          this.currentClubWatchlist[index] = {
            ...this.currentClubWatchlist[index],
            status
          }
        }

        return true
      } catch (err) {
        console.error('[storeClubs] Error updating watchlist status:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Remove item from watchlist
     * @param {string} slug - Club slug
     * @param {string} itemId - Watchlist item ID
     * @returns {boolean} Success status
     */
    async removeFromWatchlist(slug, itemId) {
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clubs/${slug}/watchlist/${itemId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          const result = await response.json()
          this.error = result.error || 'Failed to remove from watchlist'
          return false
        }

        // Remove from local state
        this.currentClubWatchlist = this.currentClubWatchlist.filter(
          (i) => i.id !== itemId
        )

        return true
      } catch (err) {
        console.error('[storeClubs] Error removing from watchlist:', err)
        this.error = err.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Clear current club state (when navigating away)
     */
    clearCurrentClub() {
      this.currentClub = null
      this.currentClubPosts = []
      this.currentClubMembers = []
      this.currentClubWatchlist = []
      this.currentClubLists = []
      this.joinRequests = []
      this.currentClubPostsOffset = 0
      this.currentClubPostsHasMore = false
    },

    /**
     * Clear all clubs state
     */
    clear() {
      this.myClubs = []
      this.myClubsLoaded = false
      this.discoverClubs = []
      this.discoverClubsLoaded = false
      this.searchResults = []
      this.searchQuery = ''
      this.clearCurrentClub()
      this.loading = false
      this.error = null

      if (realtimeSubscription) {
        supabase.removeChannel(realtimeSubscription)
        realtimeSubscription = null
      }
    },

    /**
     * Refresh after app resumes from background
     */
    async refreshAfterResume() {
      console.log('[storeClubs] Refreshing after app resume')
      await this.fetchMyClubs()
    }
  }
})
